import ProductListingLevels from '@deltek/specification-client/core/Constants/productListingLevels'
import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { ListingMatch } from '@deltek/specification-client/core/Models/listingSearch'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { SpecColumn } from '@deltek/specification-client/core/Widgets/SpecTable'
import FilterSelectText from '@deltek/specification-client/core/Widgets/SpecTable/filters/FilterSelectText'
import { Checkbox, Typography } from '@material-ui/core'
import { ListingStatus } from 'pages/ProductsPage/ProductDetailsPage/components/ListingHeaderVariants/ListingStatusVariant'
import React from 'react'
import { Row } from 'react-table'

const defaultColumnKeys = [
    'displayNameWithFirmName',
    'categories',
    'familyNumber',
    'sectionName',
    'productType',
    'productId',
    'productName',
    'type',
    'isActive',
    'submissionStatus',
]

const availableColumnKeys = [
    'listingId',
    'firmDisplayName',
    'firmId',
    'firmLogoUrl',
    'firmName',
    'hasManufacturerOptions',
    'lastUpdated',
    'canAutoGenerateProductSpecification',
    'specAgentProductTypeId',
    'partnershipLinksSM',
    'partnershipLinksULE',
]

export const columnMap: Record<
    string,
    SpecColumn<ListingMatch> & {
        isGrouped: boolean
        canExpand: boolean
        formattedText?: (value: any) => string
    }
> = {
    displayNameWithFirmName: {
        id: 'displayNameWithFirmName',
        accessor: 'displayNameWithFirmName',
        Header: 'Company Full Name',
        isGrouped: false,
        canExpand: true,
        Cell: ({ value }: { value: string }) => {
            if (value) {
                const actualString = value
                    ?.split('%|%')
                    ?.map((val) => val?.replaceAll("'", ''))
                const formattedString =
                    actualString?.[0] !== 'null'
                        ? `${actualString?.[0]} (${actualString?.[1]})`
                        : `${actualString?.[1]}`

                return (
                    <TooltipEllipsis text={formattedString ?? ''}>
                        <Typography
                            style={{
                                fontSize: pxToRem(14),
                                fontFamily: 'Roboto',
                            }}
                            noWrap
                        >
                            {formattedString}
                        </Typography>
                    </TooltipEllipsis>
                )
            } else {
                return <></>
            }
        },
        formattedText: ({ value }: { value: string }) => {
            if (value) {
                const actualString = value
                    ?.split('%|%')
                    ?.map((val) => val?.replaceAll("'", ''))
                return actualString?.[0] !== 'null'
                    ? `${actualString?.[0]} (${actualString?.[1]})`
                    : `${actualString?.[1]}`
            } else {
                return ''
            }
        },
    },
    categories: {
        id: 'categories',
        accessor: 'categories',
        Header: 'Category',
        Cell: ({ value }: { value: string[] }) => (
            <TooltipEllipsis text={value?.join(', ') ?? ''}>
                <Typography
                    style={{ fontSize: pxToRem(14), fontFamily: 'Roboto' }}
                    noWrap
                >
                    {value?.join(', ')}
                </Typography>
            </TooltipEllipsis>
        ),
        isGrouped: false,
        canExpand: true,
        formattedText: ({ value }: { value: string[] }) => {
            if (value) {
                return value?.join(', ')
            } else {
                return ''
            }
        },
    },
    familyNumber: {
        id: 'familyNumber',
        accessor: 'familyNumber',
        Header: 'Family Number',
        isGrouped: false,
        canExpand: true,
    },
    sectionName: {
        id: 'sectionName',
        accessor: 'sectionName',
        Header: 'Family Name',
        isGrouped: false,
        canExpand: true,
    },
    productType: {
        id: 'productType',
        accessor: 'productType',
        Header: 'Product Type Name',
        isGrouped: false,
        canExpand: true,
    },
    productId: {
        id: 'productId',
        accessor: 'productId',
        Header: 'Product Model Number',
        isGrouped: false,
        canExpand: true,
    },
    productName: {
        id: 'productName',
        accessor: 'productName',
        Header: 'Product Name',
        isGrouped: false,
        canExpand: true,
    },
    type: {
        id: 'type',
        accessor: 'type',
        Header: 'Listing Type',
        isGrouped: false,
        canExpand: true,
        Filter: FilterSelectText({
            options: Object.values(ProductListingLevels),
        }),
    },
    isActive: {
        id: 'isActive',
        accessor: 'isActive',
        Header: 'Status',
        isGrouped: false,
        canExpand: true,
        Cell: ({ value }: { value: any }) => {
            if (value === true || value === false) {
                return (
                    <TooltipEllipsis text={value ? 'Active' : 'Inactive'}>
                        <Typography
                            style={{
                                fontSize: pxToRem(14),
                                fontFamily: 'Roboto',
                            }}
                            noWrap
                        >
                            {value ? 'Active' : 'Inactive'}
                        </Typography>
                    </TooltipEllipsis>
                )
            } else {
                return <></>
            }
        },
        formattedText: ({ value }: { value: any }) => {
            if (value === true || value === false) {
                return value ? 'Active' : 'Inactive'
            } else {
                return ''
            }
        },
        Filter: FilterSelectText({ options: ['Active', 'Inactive'] }),
    },
    submissionStatus: {
        id: 'submissionStatus',
        accessor: 'submissionStatus',
        Header: 'State',
        isGrouped: false,
        canExpand: true,
        Filter: FilterSelectText({ options: Object.values(ListingStatus) }),
    },
    listingId: {
        id: 'listingId',
        accessor: 'listingId',
        Header: 'Listing ID',
        isGrouped: false,
        canExpand: true,
    },
    firmDisplayName: {
        id: 'firmDisplayName',
        accessor: 'firmDisplayName',
        Header: 'Company Display Name',
        isGrouped: false,
        canExpand: true,
    },
    firmId: {
        id: 'firmId',
        accessor: 'firmId',
        Header: 'Company ID',
        isGrouped: false,
        canExpand: true,
    },
    firmLogoUrl: {
        id: 'firmLogoUrl',
        accessor: 'firmLogoUrl',
        Header: 'Company Logo URL',
        isGrouped: false,
        canExpand: true,
        Cell: ({ row, value }: { row: Row; value: string }) => (
            <TooltipEllipsis text={value ?? ''}>
                <Typography
                    style={{ fontSize: pxToRem(14), fontFamily: 'Roboto' }}
                    noWrap
                >
                    {value}
                </Typography>
            </TooltipEllipsis>
        ),
        formattedText: ({ value }: { value: any }) => {
            return value
        },
    },
    firmName: {
        id: 'firmName',
        accessor: 'firmName',
        Header: 'Company Name',
        isGrouped: false,
        canExpand: true,
    },
    lastUpdated: {
        id: 'lastUpdated',
        accessor: 'lastUpdated',
        Header: 'Last Updated',
        isGrouped: false,
        canExpand: true,
    },
    canAutoGenerateProductSpecification: {
        id: 'canAutoGenerateProductSpecification',
        accessor: 'canAutoGenerateProductSpecification',
        Header: 'Auto Generate',
        isGrouped: false,
        canExpand: true,
        Cell: ({ row, value }: { row: Row; value: any }) => {
            if (value === true || value === false) {
                return (
                    <Checkbox
                        style={{ padding: 0, margin: -2 }}
                        disabled
                        checked={value}
                    />
                )
            } else return <></>
        },
        formattedText: ({ value }: { value: any }) => {
            if (value === true || value === false) {
                return value
                    ? 'Can auto-generate product specification'
                    : 'Cannot auto-generate product specification'
            } else {
                return ''
            }
        },
        Filter: FilterSelectText({
            options: ['Can auto generate', "Can't auto generate"],
        }),
    },
    specAgentProductTypeId: {
        id: 'specAgentProductTypeId',
        accessor: 'specAgentProductTypeId',
        Header: 'SA Product Type ID',
        isGrouped: false,
        canExpand: true,
    },
    partnershipLinksSM: {
        id: 'partnershipLinksSM',
        accessor: 'partnershipLinksSM',
        Header: 'Sustainable Minds',
        isGrouped: false,
        canExpand: true,
        Cell: ({ value }: { value: string }) => {
            return value && value !== 'null' ? (
                <TooltipEllipsis text={value ?? ''}>
                    <Typography
                        style={{
                            fontSize: pxToRem(14),
                            fontFamily: 'Roboto',
                        }}
                        noWrap
                    >
                        {value}
                    </Typography>
                </TooltipEllipsis>
            ) : (
                <></>
            )
        },
        formattedText: ({ value }: { value: any }) => {
            if (value && value !== 'null') {
                return value
            } else {
                return ''
            }
        },
    },
    partnershipLinksULE: {
        id: 'partnershipLinksULE',
        accessor: 'partnershipLinksULE',
        Header: 'ULE',
        isGrouped: false,
        canExpand: true,
        Cell: ({ value }: { value: string }) => {
            return value && value !== 'null' ? (
                <TooltipEllipsis text={value ?? ''}>
                    <Typography
                        style={{
                            fontSize: pxToRem(14),
                            fontFamily: 'Roboto',
                        }}
                        noWrap
                    >
                        {value}
                    </Typography>
                </TooltipEllipsis>
            ) : (
                <></>
            )
        },
        formattedText: ({ value }: { value: any }) => {
            if (value && value !== 'null') {
                return value
            } else {
                return ''
            }
        },
    },
    hasManufacturerOptions: {
        id: 'hasManufacturerOptions',
        accessor: 'hasManufacturerOptions',
        Header: 'Has Manufacturer Options',
        isGrouped: false,
        canExpand: true,
        Cell: ({ value }: Record<string, Row | boolean>) => {
            if (value === true || value === false) {
                return (
                    <Checkbox
                        style={{ padding: 0, margin: -2 }}
                        disabled
                        checked={value}
                    />
                )
            } else return <></>
        },
        formattedText: ({ value }: { value: any }) => {
            if (value === true || value === false) {
                return value ? 'True' : 'False'
            } else {
                return ''
            }
        },
        Filter: FilterSelectText({
            options: ['True', 'False'],
        }),
    },
}

export const columns = availableColumnKeys.map(
    (key) => columnMap[key]
) as SpecColumn<ListingMatch>[]

export const defaultColumns = defaultColumnKeys.map(
    (key) => columnMap[key]
) as SpecColumn<ListingMatch>[]
